import React, { useState } from 'react';
import api from '../api';
import DatenschutzCheckbox from './DatenschutzCheckbox';
import Erfolgsmeldung from './Erfolgsmeldung';
import FormularFelder, { useKachelFormular, validiereFelder, trenneWerte } from './FormularFelder';

// Abzeichen, die Seepferdchen-Voraussetzungen erfordern
const SEEPFERDCHEN_ODER_HOEHER = ['seepferdchen', 'trixi', 'bronze', 'silber', 'gold'];

const VORAUSSETZUNGEN = [
  { key: 'kann_tauchen',       label: 'Das Kind kann tauchen mit geöffneten Augen' },
  { key: 'keine_wasserangst',  label: 'Das Kind hat keine Angst vor Wasser' },
  { key: 'kann_springen',      label: 'Das Kind kann in brusthohes Wasser springen' },
];

const INIT_VORAUSSETZUNGEN = { kann_tauchen: false, keine_wasserangst: false, kann_springen: false };

export default function WartelisteFormular() {
  const { laden, fehler: ladeFehler, kachel } = useKachelFormular('warteliste');
  const felder = kachel?.formular_felder || [];

  const [werte, setWerte] = useState({});
  const [datenschutz, setDatenschutz] = useState(false);
  const [website, setWebsite] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState('');
  const [success, setSuccess] = useState(false);

  // Voraussetzungs-Checkboxen (nur für Seepferdchen+)
  const [voraussetzungen, setVoraussetzungen] = useState(INIT_VORAUSSETZUNGEN);
  const gewuenscht = werte.gewuenschtes_abzeichen || '';
  const brauchtVoraussetzungen = SEEPFERDCHEN_ODER_HOEHER.includes(gewuenscht);
  const alleVoraussetzungenErfuellt = !brauchtVoraussetzungen ||
    VORAUSSETZUNGEN.every(v => voraussetzungen[v.key]);

  const abzeichenName = (felder.find(f => f.id === 'gewuenschtes_abzeichen')?.optionen || [])
    .find(o => o.wert === gewuenscht)?.label || gewuenscht;

  const setzeWert = (id, wert) => {
    setWerte(prev => ({ ...prev, [id]: wert }));
    if (errors[id]) setErrors(prev => ({ ...prev, [id]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validiereFelder(felder, werte);
    if (!datenschutz) errs.datenschutz_akzeptiert = 'Datenschutz muss akzeptiert werden';
    if (brauchtVoraussetzungen && !alleVoraussetzungenErfuellt) {
      errs.voraussetzungen = 'Bitte alle Voraussetzungen bestätigen';
    }
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setLoading(true);
    setServerError('');
    try {
      const { system, zusatz } = trenneWerte(felder, werte);
      await api.post('/api/warteliste', {
        ...system,
        zusatzfelder: zusatz,
        datenschutz_akzeptiert: 'true',
        website,
      });
      setSuccess(true);
    } catch (err) {
      setServerError(err.response?.data?.error || 'Fehler beim Senden. Bitte versuche es später erneut.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Erfolgsmeldung
        titel="Erfolgreich eingetragen!"
        text={kachel?.bestaetigung_text || 'Vielen Dank! Du wurdest erfolgreich auf die Warteliste gesetzt. Wir melden uns, sobald ein Platz frei wird.'}
      />
    );
  }

  if (laden) return (
    <div className="form-container">
      <div style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>Lädt…</div>
    </div>
  );

  if (ladeFehler || !kachel) return (
    <div className="form-container">
      <h2 className="form-title">Warteliste</h2>
      <p style={{ color: '#c62828', textAlign: 'center', padding: '2rem' }}>
        Das Formular ist gerade nicht verfügbar. Bitte versuche es später erneut.
      </p>
    </div>
  );

  const voraussetzungenBlock = (
    <div style={{ margin: '1rem 0' }}>
      <p className="form-section-title">Voraussetzungen für {abzeichenName}</p>

      <div style={{
        background: '#e3f2fd', border: '1px solid #90caf9',
        borderRadius: 8, padding: '0.875rem 1rem', marginBottom: '1rem',
        fontSize: '0.875rem', color: '#1a237e',
      }}>
        Für die Ausbildung ab Seepferdchen-Niveau müssen folgende Grundvoraussetzungen erfüllt sein.
        Bitte bestätigen Sie jede Voraussetzung durch Anhaken.
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem', marginBottom: '0.75rem' }}>
        {VORAUSSETZUNGEN.map(v => (
          <label key={v.key} style={{
            display: 'flex', alignItems: 'flex-start', gap: '0.625rem',
            cursor: 'pointer', padding: '0.625rem 0.875rem',
            background: voraussetzungen[v.key] ? '#e8f5e9' : '#fff',
            border: `1px solid ${voraussetzungen[v.key] ? '#a5d6a7' : 'var(--border, #ddd)'}`,
            borderRadius: 8, transition: 'background 0.15s, border-color 0.15s',
          }}>
            <input
              type="checkbox"
              checked={voraussetzungen[v.key]}
              onChange={e => {
                setVoraussetzungen(prev => ({ ...prev, [v.key]: e.target.checked }));
                if (errors.voraussetzungen) setErrors(prev => ({ ...prev, voraussetzungen: '' }));
              }}
              style={{ marginTop: '0.15rem', flexShrink: 0, width: 18, height: 18 }}
            />
            <span style={{ fontSize: '0.9rem', lineHeight: 1.5 }}>{v.label}</span>
          </label>
        ))}
      </div>

      {/* Warnung wenn nicht alle erfüllt */}
      {!alleVoraussetzungenErfuellt && (
        <div style={{
          background: '#fff8e1', border: '1px solid #f9a825',
          borderRadius: 8, padding: '0.875rem 1rem', fontSize: '0.875rem',
        }}>
          <div style={{ fontWeight: 700, color: '#e65100', marginBottom: '0.4rem' }}>
            ⚠ Voraussetzungen nicht vollständig erfüllt
          </div>
          <div style={{ color: '#5d4037', lineHeight: 1.6 }}>
            Nicht alle Grundvoraussetzungen für die {abzeichenName}-Ausbildung sind erfüllt.<br />
            <strong>Wir empfehlen, das Kind stattdessen für die <span style={{ color: '#880e4f' }}>Pirat-Ausbildung</span> anzumelden.</strong>
            Die Pirat-Ausbildung vermittelt genau diese Grundfertigkeiten und bereitet optimal auf das Seepferdchen vor.
          </div>
        </div>
      )}

      {errors.voraussetzungen && (
        <span className="form-error" style={{ marginTop: '0.25rem', display: 'block' }}>
          {errors.voraussetzungen}
        </span>
      )}
    </div>
  );

  return (
    <div className="form-container">
      <h2 className="form-title">Warteliste</h2>

      {/* Hinweise zur Warteliste */}
      <div style={{
        background: '#e3f2fd', border: '1px solid #90caf9', borderRadius: 8,
        padding: '0.875rem 1rem', marginBottom: '1.25rem', fontSize: '0.875rem',
      }}>
        <div style={{ fontWeight: 700, marginBottom: '0.5rem', color: '#1565c0' }}>
          📋 Hinweise zur Warteliste
        </div>
        <ul style={{ margin: 0, paddingLeft: '1.25rem', lineHeight: 1.7, color: '#1a237e' }}>
          <li><strong>Mindestalter:</strong> Unsere Ausbildungen beginnen ab <strong>5 Jahren</strong>. Jüngere Kinder können sich bereits anmelden und werden kontaktiert, sobald sie das Mindestalter erreicht haben.</li>
          <li><strong>Reihenfolge:</strong> Die Platzvergabe erfolgt nach dem Anmeldedatum – <strong>wer sich zuerst anmeldet, erhält zuerst einen Platz</strong>.</li>
          <li><strong>Getrennte Listen:</strong> Jedes Ausbildungsziel (Seepferdchen, Bronze usw.) hat eine <strong>eigene Warteliste</strong>. Die Anmeldung gilt nur für das gewählte Abzeichen.</li>
          <li><strong>Benachrichtigung:</strong> Wir melden uns per E-Mail oder Telefon, sobald ein Platz in deiner Kategorie frei wird.</li>
        </ul>
      </div>

      {serverError && <div className="alert alert-error">{serverError}</div>}

      <form onSubmit={handleSubmit} noValidate>
        <div className="honeypot" aria-hidden="true">
          <input type="text" name="website" value={website} onChange={e => setWebsite(e.target.value)} tabIndex="-1" autoComplete="off" />
        </div>

        <FormularFelder
          felder={felder}
          werte={werte}
          fehler={errors}
          onChange={setzeWert}
          nachFeld={f => (f.id === 'gewuenschtes_abzeichen' && brauchtVoraussetzungen ? voraussetzungenBlock : null)}
        />

        <DatenschutzCheckbox
          checked={datenschutz}
          onChange={e => { setDatenschutz(e.target.checked); if (errors.datenschutz_akzeptiert) setErrors(p => ({ ...p, datenschutz_akzeptiert: '' })); }}
          error={errors.datenschutz_akzeptiert}
        />

        <button type="submit" className="submit-btn"
          disabled={loading || (brauchtVoraussetzungen && !alleVoraussetzungenErfuellt)}>
          {loading ? 'Wird gesendet…'
            : brauchtVoraussetzungen && !alleVoraussetzungenErfuellt
              ? 'Bitte alle Voraussetzungen bestätigen'
              : 'Auf die Warteliste setzen'}
        </button>
      </form>
    </div>
  );
}
